/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useState, useContext, useMemo } from 'react'
import {
    exportCategorySet,
    getCategorySets,
    isCategoryProvidedByUserProvider,
} from '../../services/Category/Category.service'
import { Tab, Form, Button, Icon, Card, Label, Divider, Grid } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { UltyModalWrapperContext } from '../Shared/UltyModalWrapper/UltyModalWrapperContext'
import CategoryAddSet from './CategoryAddSet'
import CategoryRemoveSet from './CategoryRemoveSet'
import { useTranslation } from 'react-i18next'
import CategoryLinks from './CategoryLinks'
import useUser from '../Shared/UserProvider/useUser'
import { notify } from 'react-notify-toast'
import { saveFile } from '../../services/File/File.service'
import useBreadcrumb from '../NavBar/useBreadcrumb'
import { isProviderACompany, isProviderAPos } from '../../services/Provider/Provider.service'
import { useMutation } from '@tanstack/react-query'
import { sortDesc } from '../../helpers/array'
import { Outlet, useLocation, useNavigate } from 'react-router-dom-v5-compat'
import useFeature from '../Shared/FeatureProvider/useFeature'
import slugify from 'slugify'

const CategorySet = () => {
    const {user, canCreateCategories, canExtendCategories} = useUser()
    const [t] = useTranslation()
    const [loading, setLoading] = useState(false)
    const [sets, setSets] = useState([])
    const {setPaths} = useBreadcrumb();

    const {isEnabled} = useFeature();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.categories'),
            link: false,
        }])
    }, [])

    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)
    const defineAddModal = () => {
        const modalSettings = {
            title: t('category_set.new_set'),
            component: <CategoryAddSet
              afterSubmit={() => {
                  loadCategorySets()
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }
        handleUltyModalWrapper(true, modalSettings)
    }
    const defineRemoveModal = (s) => {
        const modalSettings = {
            title: t('category_set.delete_set'),
            component: <CategoryRemoveSet
              set={s}
              afterSubmit={() => {
                  loadCategorySets()
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }
        handleUltyModalWrapper(true, modalSettings)
    }

    const loadCategorySets = async () => {
        setLoading(true)

        const sets = await getCategorySets();
        const withHavingLinksFirst = sortDesc(sets, s => s.hasLink);
        setSets(withHavingLinksFirst)

        setLoading(false)
    }

    useEffect(() => {
        loadCategorySets()
    }, [])

    const exportCategorySetMutation = useMutation({
        mutationFn: async ({ set }) => {
            const { data } = await exportCategorySet(set.id)
            return saveFile(`${slugify(set.name)}_${t('category_set.export_set_file_name')}`, data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        },
        onError: (error) => {
            console.log(error)
            notify.show(t('global.anErrorOccurred'), 'error')
        },
    })

    const CategorySetCard = ({s}) => (
                              <Card key={s.id} css={css` margin: 1.2em 1.5em !important; `} >
                                  <Card.Content>
                                      <div css={css`
                                          margin-bottom: 10px;
                                      `}>
                                          {s.hasLink && <Label css={css`opacity:0.5`} size="small" color='white' corner="left" icon="linkify"/> }

                                          {s.provider.type === 'COMPANY' ? (
                                            <Label
                                                size="mini" ribbon="right" color="blue"
                                                >
                                                {t('global.company')}
                                            </Label>
                                          ) : (
                                            <Label size="mini" ribbon="right" color="green"
                                            >
                                                {t('global.merchant')}
                                            </Label>
                                          )}
                                      </div>
                                      <Card.Header as={Link} to={`/categories/${s.id}`} css={css`
                                          margin-top: 10px;
                                      `}>{s.name}</Card.Header>
                                      <Card.Meta>{(new Date(s.updatedAt)).toLocaleDateString()}</Card.Meta>
                                      <Card.Description>
                                          <Grid>
                                              <Grid.Column width={16} textAlign="left" css={css`span {
                                                  font-weight: bold;
                                              }`}>
                                                  <div>
                                                      <label>{t('category_set.slot_number')}: </label><span>{s.slots.length}</span>
                                                  </div>
                                                  <div>
                                                      <label>{t('category_set.item_number')}: </label><span>{s.itemsCount || 'N/A'}</span>
                                                  </div>
                                              </Grid.Column>
                                          </Grid>
                                      </Card.Description>
                                  </Card.Content>
                                  <Card.Content extra css={css`text-align: right;`}>
                                      <div css={css`
                                          display: flex;
                                          justify-content: flex-end;
                                      `}>
                                          {isCategoryProvidedByUserProvider(s, user) &&
                                            <Button basic size="mini" color="red"
                                                    onClick={() => defineRemoveModal(s)}>{t('global.delete')}</Button>
                                          }
                                          <Button basic size="mini" color="teal" as={Link}
                                                  to={`/categories/${s.id}`}>{((isProviderAPos(user.provider) && !canExtendCategories()) || !isCategoryProvidedByUserProvider(s, user)) ? t('global.see') : t('global.edit')}</Button>
                                          {isCategoryProvidedByUserProvider(s, user) &&
                                            <Button
                                              basic
                                              size="mini"
                                              color="teal"
                                              loading={exportCategorySetMutation.isPending}
                                              onClick={() => exportCategorySetMutation.mutate({ set: s })}
                                            >
                                                <Icon name={'file excel'}/>
                                                {t('category_set.export')}
                                            </Button>
                                          }
                                      </div>
                                  </Card.Content>
                              </Card>
    )

    const panes = [
              {
                  menuItem: t('category_set.manage_menu'),
                  path: '/categories',
                  render: () => (
                    <Tab.Pane attached={false} loading={loading}>
                        {canCreateCategories() && (
                          <>
                              <Form.Group widths="equal" css={css`margin-bottom: 0 !important;`}>
                                  <Form.Field inline css={css`text-align: right;`}>
                                      <Button icon color="teal" onClick={() => defineAddModal()}>
                                          <Icon name="copy"/>
                                          {t('category_set.new_set')}
                                      </Button>
                                  </Form.Field>
                              </Form.Group>
                              <Divider/>
                          </>
                        )}
                        <Card.Group css={css`
                            justify-content: center;
                        `}>
                            {sets.map(s => <CategorySetCard s={s}/>)}
                        </Card.Group>
                    </Tab.Pane>
                  ),
              },
              {
                  menuItem: t('category_set.manage_link'),
                  path: '/categories/publications',
                  render: () => (
                    <Tab.Pane attached={false} loading={loading}>
                        <div css={css`margin-top: 10px;`}>
                            <CategoryLinks categorySets={sets}/>
                        </div>
                    </Tab.Pane>
                  ),
              },
              ...((isEnabled('promotion_campaigns') && isProviderACompany(user.provider)) ? [{
                  menuItem: t('promotion_campaign.header'),
                  path: '/categories/promotion-campaigns',
                  render: () => null,
              },] : [])
    ];

    // Find the tab that matches the current pathname
        const activeIndex = useMemo(() => {
        const sortedPanes = sortDesc([...panes], p => p.path?.length || 0); // Sort by path length descending to prioritize more specific paths
        const match = sortedPanes.find(pane => {
            if (!pane.path) return false;
            return location.pathname.startsWith(pane.path);
        });
        const matchIndex = panes.indexOf(match);
        return matchIndex >= 0 ? matchIndex : 0;
        }, [location.pathname]);

    return (
        <>
      <Form>
          <Tab
          
          activeIndex={activeIndex}
          menu={{secondary: true, pointing: true}}
          onTabChange={(_, data) => {
          const chosen = panes[data.activeIndex];
          if(chosen.path){
              navigate(chosen.path);
          }else if(location.pathname !== '/categories') {
             navigate('')
          }
        }}
          
          panes={panes}/>
       
      </Form>
       <div style={{ marginTop: '1em' }}>
            <Outlet />
        </div>
      </>
    )
}

export default CategorySet
